import React from 'react';
import { Box, Typography, LinearProgress, IconButton, Snackbar, Tooltip, Button } from '@mui/material';
import { Visibility, VisibilityOff, ContentCopy } from '@mui/icons-material';

const getFriendlyTimeToCrack = (time, entropy) => {
  if (entropy < 40) return 'Seconds to guess this weak password';
  if (time < 60) return 'Less than a minute to guess this password';
  if (time < 3600) return 'A few minutes to guess this password'; // < 1 hour
  if (time < 86400) return 'A few hours to guess this password'; // < 1 day
  if (time < 604800) return 'A few days to guess this password'; // < 1 week
  if (time < 2592000) return 'A few weeks to guess this password'; // < 1 month
  if (time < 31536000) return 'A few months to guess this password'; // < 1 year
  if (time < 315360000) return 'A few years to guess this password'; // < 10 years
  if (time < 3153600000) return 'Decades to guess this password'; // < 100 years
  if (time < 31536000000) return 'Centuries to guess this password'; // < 1,000 years
  return 'Eons to guess this password'; // > 1,000 years
};

const PasswordDisplay = ({
  password = '',
  entropy = 0,
  timeToCrack = 0,
  onRegenerate,
  hideRegenerate = false, // Optional prop to toggle regenerate button visibility
  viewPassword = true,
}) => {
  const [showPassword, setShowPassword] = React.useState(viewPassword);
  const [copySuccess, setCopySuccess] = React.useState(false);

  const strength =
    password === '' ? 'Weak' : entropy < 40 ? 'Weak' : entropy < 60 ? 'Moderate' : 'Strong';
  const barColor = entropy < 40 ? '#f44336' : entropy < 60 ? '#ffeb3b' : '#4caf50';

  const handleCopy = () => {
    if (password) {
      navigator.clipboard.writeText(password);
      setCopySuccess(true);
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        borderRadius: '16px',
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
        padding: '1.5rem',
        textAlign: 'center',
      }}
    >
      {/* Password Display */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          marginBottom: '1rem',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
      >
        {/* Copy Icon */}
        <IconButton
          onClick={handleCopy}
          sx={{ color: 'text.secondary' }}
          aria-label="Copy password"
        >
          <ContentCopy />
        </IconButton>

        {/* Hide/Show Password */}
        <IconButton
          onClick={() => setShowPassword((prev) => !prev)}
          sx={{ color: 'text.secondary' }}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          {showPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>

        {/* Password Text */}
        <Tooltip title={password || 'Empty password'} arrow disableHoverListener={!showPassword}>
          <Typography
            sx={{
              fontFamily: 'monospace',
              color: 'text.primary',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              flexGrow: 1,
              fontSize: password.length <= 20 ? '1.5rem' : password.length <= 40 ? '1.25rem' : '1rem',
            }}
          >
            {showPassword ? password || 'Empty' : '*'.repeat(password?.length || 0)}
          </Typography>
        </Tooltip>
      </Box>

      {/* Password Strength */}
      <Box sx={{ marginBottom: '1.5rem', textAlign: 'center' }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 'bold',
            marginBottom: '0.5rem',
            color: barColor,
          }}
        >
          Password Strength: {strength}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={Math.min((entropy / 128) * 100, 100)}
          sx={{
            height: '16px',
            width: '85%',
            margin: '0 auto',
            borderRadius: '8px',
            backgroundColor: '#ddd',
            '& .MuiLinearProgress-bar': {
              backgroundColor: barColor,
            },
          }}
        />
      </Box>

      {/* Time-to-Crack */}
      <Typography
        sx={{
          fontSize: '1rem',
          fontWeight: 'bold',
          color: 'text.secondary',
        }}
      >
        {getFriendlyTimeToCrack(timeToCrack, entropy)}
      </Typography>

      {/* Regenerate Button */}
      {!hideRegenerate && onRegenerate && (
        <Button
          onClick={onRegenerate}
          variant="contained"
          sx={{
            marginTop: '1.5rem',
            padding: '0.6rem 2rem',
            borderRadius: '24px',
            backgroundColor: 'primary.main',
            color: '#FFFFFF',
            fontWeight: 'bold',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: 'primary.dark',
              boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
            },
          }}
        >
          Regenerate
        </Button>
      )}

      {/* Copy Snackbar */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={2000}
        message="Password copied!"
        onClose={() => setCopySuccess(false)}
      />
    </Box>
  );
};

export default PasswordDisplay;
